import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Loader2, Download, MessageCircle, Sparkles, CheckCircle2, Volume2 } from "lucide-react";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const OCCASIONS = [
  "Dua personnel",
  "Mariage",
  "Anniversaire",
  "Naissance / aqîqa",
  "Bénédiction départ",
  "Message corporate",
  "Annonce / présentation",
  "Autre",
];

export default function VoiceGift() {
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [pkg, setPkg] = useState("court");
  const [occasion, setOccasion] = useState("Dua personnel");
  const [recipientName, setRecipientName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [message, setMessage] = useState("Salaam aleykum… ");
  const [preset, setPreset] = useState("studio");
  const [language, setLanguage] = useState("fr");
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/voice-gift/packages`).then((r) => setPackages(r.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Récupération de session_id après retour Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/voice-gift/status/${sid}`);
        if (data.ready && data.audio_url) {
          setResult({ session_id: sid, audio_url: `${BACKEND}${data.audio_url}` });
          setPolling(false);
          toast.success(T("Ton message vocal est prêt 🌹"));
          return true;
        }
        return false;
      } catch { return false; }
    };
    let attempts = 0;
    const id = setInterval(async () => {
      attempts += 1;
      const done = await check();
      if (done || attempts > 30) { clearInterval(id); setPolling(false); }
    }, 2500);
    check();
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const selectedPkg = packages.find((p) => p.id === pkg);
  const maxChars = selectedPkg?.max_chars || 350;
  const charsLeft = maxChars - message.length;

  const checkout = async () => {
    if (!recipientName.trim()) { toast.error(T("Prénom du destinataire requis.")); return; }
    if (!buyerEmail.trim()) { toast.error(T("Ton email requis.")); return; }
    if (!message.trim() || message.length < 20) { toast.error("Message trop court."); return; }
    if (message.length > maxChars) { toast.error(`Trop long : ${message.length}/${maxChars} caractères`); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/voice-gift/checkout`, {
        package_id: pkg,
        occasion,
        recipient_name: recipientName,
        message,
        buyer_email: buyerEmail,
        preset,
        language,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Échec checkout."));
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-voice-gift" className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)] inline-flex items-center gap-2">
        <Mic size={14} className="text-[color:var(--papicoo-gold)]" /> {T("MESSAGES VOCAUX PERSONNALISÉS")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Offre ma voix.")}<br />
        <span className="italic font-light">{T("Force tranquille, dans l'oreille.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl text-sm sm:text-base">
        {T("Papicoo enregistre pour toi un message audio personnalisé avec sa voix radio FM studio : un dua, une bénédiction de mariage, un message corporate, une annonce. Tu reçois le MP3 par email — partageable WhatsApp instantanément.")}
      </p>

      {/* Résultat après paiement */}
      {result && (
        <div data-testid="voice-gift-result" className="mt-8 papicoo-card p-6 bg-emerald-50 border-l-4 border-emerald-500 space-y-4">
          <div className="inline-flex items-center gap-2 font-medium text-emerald-700">
            <CheckCircle2 size={18} /> {T("Ton message vocal est prêt 🌹")}
          </div>
          <audio data-testid="voice-gift-audio" controls className="w-full" src={result.audio_url} />
          <div className="flex gap-2 flex-wrap">
            <a href={result.audio_url} download className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full papicoo-btn-primary text-sm">
              <Download size={14} /> {T("Télécharger")}
            </a>
            <a href={`https://wa.me/?text=${encodeURIComponent("Salaam — un message vocal de Papicoo pour toi : " + result.audio_url)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm">
              <MessageCircle size={14} /> {T("Partager WhatsApp")}
            </a>
          </div>
        </div>
      )}

      {polling && (
        <div className="mt-8 papicoo-card p-6 bg-amber-50 border-l-4 border-amber-500 inline-flex items-center gap-3">
          <Loader2 className="animate-spin" /> {T("Génération de ton message vocal en cours (≈ 20-40s)…")}
        </div>
      )}

      {!result && !polling && (
        <div className="mt-8 grid lg:grid-cols-[1fr_1.1fr] gap-6">
          {/* Choix de pack */}
          <div className="space-y-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("Choisis ton format")}</div>
            <div className="grid gap-3">
              {packages.map((p) => (
                <button
                  key={p.id}
                  data-testid={`pack-${p.id}`}
                  onClick={() => setPkg(p.id)}
                  className={`papicoo-card p-4 text-left transition ${pkg === p.id ? "ring-2 ring-[color:var(--papicoo-gold)] bg-white" : "hover:shadow-md bg-white/70"}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-display text-xl text-[color:var(--papicoo-ink)]">{T(p.label)}</div>
                      <div className="text-xs text-[color:var(--papicoo-muted)] mt-1">{T(p.desc)}</div>
                    </div>
                    <div className="text-2xl font-display text-[color:var(--papicoo-gold)] shrink-0">{p.price_eur} €</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Formulaire */}
          <div className="papicoo-card p-5 space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)] mb-2">{T("Détails du message")}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              <select data-testid="vg-occasion" value={occasion} onChange={(e) => setOccasion(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm">
                {OCCASIONS.map((o) => <option key={o} value={o}>{T(o)}</option>)}
              </select>
              <input data-testid="vg-recipient" placeholder={T("Prénom du destinataire (ex: Mariama)")} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
            </div>
            <input data-testid="vg-email" type="email" placeholder={T("Ton email (pour recevoir le MP3)")} value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm" />
            <div className="grid sm:grid-cols-2 gap-2">
              <select data-testid="vg-language" value={language} onChange={(e) => setLanguage(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm">
                <option value="fr">{T("🇫🇷 Français")}</option>
                <option value="ar">العربية</option>
                <option value="wo">{T("Wolof")}</option>
              </select>
              <select data-testid="vg-preset" value={preset} onChange={(e) => setPreset(e.target.value)} className="px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm">
                <option value="studio">{T("Radio FM studio (présidentiel)")}</option>
                <option value="intimate">{T("Intimiste / podcast")}</option>
              </select>
            </div>
            <textarea
              data-testid="vg-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={7}
              maxLength={maxChars + 100}
              placeholder={T("Le message que Papicoo doit dire (max {n} caractères). Tu peux inclure le prénom : « Salaam Mariama, en ce jour béni… »").replace("{n}", maxChars)}
              className="w-full px-3 py-2 rounded-lg border border-[color:var(--papicoo-border)] text-sm leading-relaxed"
            />
            <div className={`text-[11px] text-right ${charsLeft < 0 ? "text-red-600" : "text-[color:var(--papicoo-muted)]"}`}>
              {message.length} / {maxChars} caractères
            </div>
            <button
              data-testid="vg-checkout"
              onClick={checkout}
              disabled={loading || charsLeft < 0}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full papicoo-btn-gold text-sm disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : <Volume2 size={14} />}
              {loading ? "Redirection Stripe…" : `Payer ${selectedPkg?.price_eur || 9}€ et recevoir mon message`}
            </button>
            <p className="text-[10px] text-[color:var(--papicoo-muted)] italic text-center">
              {T("Paiement sécurisé via Stripe. Tu recevras le MP3 par email après paiement.")}
              <br />{T("Aucun stockage de carte, aucun renouvellement automatique.")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
